import {
  Delete,
  FileUpload,
  GetApp,
  Settings,
  Sync,
} from "@mui/icons-material";
import {
  alpha,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useMapActions } from "../../../contexts/mapActionsContext";
import useMapMenu from "../../../hooks/useMapMenu";
import CsvPreviewModal from "../../../componentes/easyMaps/titleBar/modals/CsvPreviewModal";
import JsonPreviewModal from "../../../componentes/easyMaps/titleBar/modals/JsonPreviewModal";
import PasswordModal from "../../../componentes/easyMaps/titleBar/modals/PasswordModal";
import AlertModal from "../../../componentes/alertModal";
import usePermissao from "../../../hooks/usePermissao";

export default function Config({ setOpenConfig = () => {} }) {
  const theme = useTheme();
  const { menuConfig } = useMapActions();

  const menu = useMapMenu(menuConfig);
  const { canEdit } = usePermissao("easyMaps");

  const styles = {
    dotsIcon: {
      padding: "3px",
      fontSize: "29px",
      fill: theme.palette.text.primary,
      ":hover": { opacity: 1, transform: "rotate(90deg)" },
    },
    menuIcons: {
      color: alpha(theme.palette.primary.main, 0.7),
      fontSize: 20,
    },
    menuItem: { gap: 0.5, ml: -1 },
  };

  const handleConfigClick = () => {
    setOpenConfig((data) => ({ open: !data.open, tab: 0 }));
  };
  console.log(menu.userPermission);

  if (!canEdit) return null;

  if (!menuConfig) {
    if (menu.userPermission !== "adm002" && menu.userPermission !== "adm001")
      return null;
    return (
      <Tooltip title="Configurações Globais">
        <IconButton
          color="inherit"
          aria-label="configuracoes"
          onClick={handleConfigClick}
        >
          <Settings sx={styles.dotsIcon} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title="Configurações">
        <IconButton
          color="inherit"
          aria-label="configuracoes"
          onClick={menu.handleMenuOpen}
        >
          <Settings sx={styles.dotsIcon} />
        </IconButton>
      </Tooltip>

      <input
        ref={menu.fileInputRef}
        hidden
        type="file"
        accept=".xls, .xlsx, .csv"
        onChange={(e) => {
          menu.handleImportCsv(e);
          e.target.value = "";
        }}
      />
      <input
        ref={menu.fileJsonInputRef}
        hidden
        type="file"
        accept=".json"
        onChange={(e) => {
          menu.handleImportJson(e);
          e.target.value = "";
        }}
      />

      <Menu
        anchorEl={menu.anchorEl}
        open={menu.menuOpen}
        onClose={menu.handleMenuClose}
        MenuListProps={{ "aria-labelledby": "config-button" }}
        sx={{
          "& .MuiPopover-paper": {
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        {menu.exportImport && [
          <MenuItem
            key="export"
            onClick={(e) => menu.setExportAnchor(e.currentTarget)}
            sx={styles.menuItem}
          >
            <GetApp sx={styles.menuIcons} />
            Exportar
          </MenuItem>,
          <Menu
            key="export-menu"
            anchorEl={menu.exportAnchor}
            open={Boolean(menu.exportAnchor)}
            onClose={() => menu.setExportAnchor(null)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <MenuItem
              onClick={() => {
                menu.setExportAnchor(null);
                menu.handleMenuClose();
                menu.handleExport();
              }}
            >
              Baixar como CSV
            </MenuItem>
            {(menu.userPermission === "adm001" ||
              menu.userPermission === "adm002") && (
              <MenuItem
                onClick={() => {
                  menu.setExportAnchor(null);
                  menu.handleMenuClose();
                  menu.handleExportJson();
                }}
              >
                Baixar como JSON
              </MenuItem>
            )}
          </Menu>,
          <MenuItem
            key="import"
            onClick={(e) => menu.setImportAnchor(e.currentTarget)}
            sx={styles.menuItem}
          >
            <FileUpload sx={styles.menuIcons} />
            Importar
          </MenuItem>,
          <Menu
            key="import-menu"
            anchorEl={menu.importAnchor}
            open={Boolean(menu.importAnchor)}
            onClose={() => menu.setImportAnchor(null)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <MenuItem
              onClick={() => {
                menu.setImportAnchor(null);
                menu.fileInputRef.current && menu.fileInputRef.current.click();
              }}
            >
              Importar como CSV
            </MenuItem>
            {(menu.userPermission === "adm001" ||
              menu.userPermission === "adm002") && (
              <MenuItem
                onClick={() => {
                  menu.setImportAnchor(null);
                  menu.fileJsonInputRef.current &&
                    menu.fileJsonInputRef.current.click();
                }}
              >
                Importar como JSON
              </MenuItem>
            )}
          </Menu>,
        ]}
        <MenuItem
          onClick={() => {
            menu.setOpenDeleteModal(true);
          }}
          sx={styles.menuItem}
        >
          <Delete sx={styles.menuIcons} />
          Deletar
        </MenuItem>
        <MenuItem
          onClick={() => {
            menu.setOpenPasswordModal(true);
          }}
          sx={styles.menuItem}
        >
          <Tooltip title="Sincronizar elementos com o Zabbix">
            <Stack sx={{ display: "flex", flexDirection: "row", gap: 0.5 }}>
              <Sync sx={styles.menuIcons} />
              <Typography>Sincronizar</Typography>
            </Stack>
          </Tooltip>
        </MenuItem>
        {(menu.userPermission === "adm001" ||
          menu.userPermission === "adm002") && (
          <MenuItem onClick={handleConfigClick} sx={styles.menuItem}>
            <Settings sx={styles.menuIcons} />
            Configurações
          </MenuItem>
        )}
      </Menu>

      <CsvPreviewModal
        open={menu.previewCsvModal}
        onClose={() => menu.setPreviewCsvModal(false)}
        elementData={menu.elementData}
        duplicatedRows={menu.duplicatedRows}
        importElements={menu.importElements}
      />
      <JsonPreviewModal
        open={menu.previewJsonModal}
        onClose={() => {
          menu.setPreviewJsonModal(false);
          menu.setJsonPreviewData(null);
        }}
        jsonData={menu.jsonPreviewData}
        importJson={async (parsed) => {
          await menu.importJsonFromPreview(parsed);
        }}
        modalStyle={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          bgcolor: "background.default",
          boxShadow: 24,
          p: 4,
        }}
        title="Preview JSON"
        isPing={menu.isPing}
      />
      <AlertModal
        openModal={menu.openDeleteModal}
        setOpenModal={menu.setOpenDeleteModal}
        handleButton={menu.handleApiDeleteMap}
        severity={"error"}
        singleButton={false}
      />
      <PasswordModal
        openModal={menu.openPasswordModal}
        setOpenModal={menu.setOpenPasswordModal}
        handleSynchronizeElements={menu.handleSynchronizeElements}
      />
    </>
  );
}
